import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import { useTranslation } from 'react-i18next';

import { useMainHubStoreAvailableResponseDataState } from '@/store/main-hub.store';
import { VIEW_TYPE, ViewType } from '@/store/view.type';
import { useModalStore } from '@/components/modal/modal.store';

import { RenderDetailByType } from './components/render-detail-by-type';
import { getPlatformUrl } from './helpers/get-platform-url';
import { getSettingsUrl } from './helpers/get-settings-url';

type AvailableViewType = Exclude<
  ViewType,
  typeof VIEW_TYPE.APP_LOADING | typeof VIEW_TYPE.SERVICE_NOT_AVAILABLE
>;

const isAllowedViewType = (value: unknown): value is AvailableViewType =>
  typeof value === 'string' &&
  (Object.values(VIEW_TYPE) as string[]).includes(value) &&
  value !== VIEW_TYPE.APP_LOADING &&
  value !== VIEW_TYPE.SERVICE_NOT_AVAILABLE;

interface RecipientDetailsModalProps {
  viewType?: ViewType;
}

export function RecipientDetailsModal({ viewType }: RecipientDetailsModalProps) {
  const { t } = useTranslation();
  const { additionalProps } = useModalStore();
  let resolvedViewType: AvailableViewType = VIEW_TYPE.LOAN_FORM_VIEW;
  if (isAllowedViewType(viewType)) {
    resolvedViewType = viewType;
  } else if (isAllowedViewType(additionalProps?.viewType)) {
    resolvedViewType = additionalProps.viewType;
  }
  const {
    merchantResponseData: { data },
  } = useMainHubStoreAvailableResponseDataState(resolvedViewType);

  const platformUrl = getPlatformUrl();
  const settingsUrl = getSettingsUrl(data.merchantData, platformUrl);

  return (
    <Box
      data-testid="recipient-details"
      sx={{
        mt: '32px',
      }}
    >
      <Typography component="p" marginBottom="20px" variant="h6">
        {t('loanFormPage.recipientDetails.header')}
      </Typography>
      <Typography marginBottom="10px" variant="body1">
        {t('loanFormPage.recipientDetails.dataInfo')}
      </Typography>
      <Typography marginBottom="10px">{t('loanFormPage.recipientDetails.phoneNumber')}</Typography>
      <RenderDetailByType
        sx={{ marginBottom: 2 }}
        detailType={{
          link: {
            isEdit: true,
            name: 'phone',
            href: settingsUrl,
          },
        }}
      />
      <Typography marginBottom="10px">{t('loanFormPage.recipientDetails.emailAddress')}</Typography>
      <RenderDetailByType
        sx={{ marginBottom: 2 }}
        detailType={{
          link: {
            isEdit: true,
            name: 'mail',
            href: settingsUrl,
          },
        }}
      />
      <Typography marginBottom="10px">{t('loanFormPage.recipientDetails.personalData')}</Typography>
      <Typography
        sx={{
          my: '10px ',
        }}
      >
        {t('loanFormPage.recipientDetails.contactByMail')}
      </Typography>
      <Link href="mailto:biznes@przelewy24.pl" underline="none">
        financing-support@przelewy24.pl
      </Link>
      <Stack
        sx={{
          my: '10px',
        }}
      >
        {t('loanFormPage.recipientDetails.contactByPhone')}
      </Stack>
      <Link href="tel:+48616429080" underline="none">
        +48 616 429 080
      </Link>{' '}
      {t('loanFormPage.recipientDetails.hours')}
    </Box>
  );
}
